import {AfterViewInit, Component, Input, ViewChild} from '@angular/core';
import {MatSidenav} from "@angular/material/sidenav";
import {MenuItem} from "./model/menu-item";
import {BreakpointObserver} from "@angular/cdk/layout";
import {ChangePasswordComponent} from "../change-password/change-password.component";
import {AuthenticationService} from "../../services/authentication.service";
import {appConfig} from "../../app-config";
import {User} from "../../models/user";
import {OrganizationService} from "../../services/organization.service";
import {ProdDialogData, ProdDialogService, ProdDialogType} from 'prod-dialog';
import {Help} from "../help/help";
import {RoleService} from "../../services/role.service";
import {StorageService} from 'prod-shared';
import {Location} from "@angular/common";
import {Router} from "@angular/router";
import {TranslateService} from "@ngx-translate/core";

@Component({
  selector: 'app-side-menu',
  templateUrl: './side-menu.component.html',
  styleUrls: ['./side-menu.component.scss']
})

export class SideMenuComponent implements AfterViewInit {
  appTitle = this.translate.instant('menu.app_title');
  openedMenu: string = '';
  @ViewChild('sidenav') sidenav: MatSidenav;
  @Input() user: User;
  organizationService = OrganizationService;
  isExpanded: boolean = appConfig.sidenav_default_open;
  isMenuTextShown: boolean = appConfig.sidenav_default_open;
  help: string = this.translate.instant('menu.help');
  navigation: MenuItem[] = [
    new MenuItem('', this.translate.instant('menu.dashboard'), 'dashboard'),
    new MenuItem('organizations', this.translate.instant('menu.organizations'), 'account_tree', ['ROLE_SYS']),
    new MenuItem('company', this.translate.instant('menu.company'), 'other_houses'),
    new MenuItem('distributor', this.translate.instant('menu.distributor'), 'business_center', ['ROLE_DISTRIBUTOR']),
    new MenuItem('vehicles', this.translate.instant('menu.vehicles'), 'directions_car_filled'),
    new MenuItem('firmwares', this.translate.instant('menu.firmwares'), 'cloud_upload', ['ROLE_SYS']),
    new MenuItem('users', this.translate.instant('menu.users'), 'supervisor_account', ['ROLE_SYS']),
    new MenuItem('', this.translate.instant('menu.logs.main_title'), 'list_alt', [], true, [
      new MenuItem('communicationLogs', this.translate.instant('menu.logs.communication'), 'commute'),
      new MenuItem('deviceEvents', this.translate.instant('menu.logs.device_events'), 'perm_device_info'),
      new MenuItem('auditLogs', this.translate.instant('menu.logs.audit'), 'list', ['ROLE_SYS'])
    ])
  ]

  constructor(
    private observer: BreakpointObserver,
    private authenticationService: AuthenticationService,
    private roleService: RoleService,
    private prodDialogService: ProdDialogService,
    private storageService: StorageService,
    private location: Location,
    private router: Router,
    private translate: TranslateService
  ) {
  }

  ngOnInit() {
    let hiddenMenuRouterLinks: string[] = [];
    this.navigation.forEach(nav => {
      if (nav.routerLink === 'cars' && this.user.distributor) {
        let orgStatuses = this.user.organizationStatus;
        delete orgStatuses[this.user.distributor.id];
        if (!Object.values(orgStatuses).includes(true)) {
          hiddenMenuRouterLinks.push(nav.routerLink);
        }
      }
      if (nav.roles.length > 0 && !this.roleService.hasRole(nav.roles)) {
        hiddenMenuRouterLinks.push(nav.routerLink);
      }
      if (nav.subMenuItems) {
        let hiddenSubMenuRouterLinks: string[] = [];
        nav.subMenuItems.forEach(subNav => {
          if (subNav.roles.length > 0 && !this.roleService.hasRole(subNav.roles)) {
            hiddenSubMenuRouterLinks.push(subNav.routerLink);
          }
        });
        nav.subMenuItems = nav.subMenuItems.filter(sub => hiddenSubMenuRouterLinks.indexOf(sub.routerLink) === -1);
      }
    });

    this.navigation = this.navigation.filter(nav => hiddenMenuRouterLinks.indexOf(nav.routerLink) === -1);
  }

  ngAfterViewInit() {
    this.observer.observe(['(max-width: 800px)']).subscribe((res) => {
      if (res.matches) {
        this.sidenav.mode = 'over';
        this.sidenav.close();
      } else {
        this.sidenav.mode = 'side';
        this.sidenav.open();
      }
    });

    let activeLink = this.location.path().substring(1);
    this.navigation.forEach(nav => {
      if (nav.subMenuItems && nav.subMenuItems.length) {
        nav.subMenuItems.forEach(sub => {
          if (sub.routerLink === activeLink) {
            this.openedMenu = nav.name;
          }
        });
      }
    });
  }

  mouseenter() {
    if (!this.isExpanded) {
      this.isMenuTextShown = true;
    }
  }

  mouseleave() {
    if (!this.isExpanded) {
      this.isMenuTextShown = false;
    }
  }

  onClickToggleMenu() {
    this.isExpanded = !this.isExpanded;
    if (!this.isExpanded) this.isMenuTextShown = false;
  }

  onClickChangePassword() {
    if (this.sidenav.mode === 'over') this.sidenav.toggle();
    this.prodDialogService.open(new ProdDialogData({
      component: ChangePasswordComponent,
      type: ProdDialogType.FORM
    }))
  }

  onClickLogout() {
    this.authenticationService.logout();
  }

  openHelpDialog() {
    if (this.sidenav.mode === 'over') this.sidenav.toggle();
    this.prodDialogService.open(new ProdDialogData({
      component: Help,
      type: ProdDialogType.COMPONENT,
      styles: {width: '30%', height: '60%'}
    }));
  }

  isDisabled(menuItem: MenuItem) {
    return menuItem.roles && menuItem.roles.length ? !this.roleService.hasRole(menuItem.roles) : false;
  }

  toggleMenu(name: string): void {
    if (this.openedMenu === name) {
      this.openedMenu = '';
    } else {
      this.openedMenu = name;
    }
  }

  isOver() {
    return this.sidenav && this.sidenav.mode === 'over';
  }

  onClickMenuItem(nav) {
    if (this.router.url.split('/')[1] === nav.routerLink) {
      this.router.navigateByUrl('/reloadPage', {skipLocationChange: true}).then(() => {
        setTimeout(_ => this.router.navigate([nav.routerLink]), 10)
      });
    }
    if (this.sidenav.mode === 'over') this.sidenav.toggle()
  }
}
